import { Button } from "@material-ui/core";
import TableRow from "@material-ui/core/TableRow";
import clsx from "clsx";
import { Form, Formik } from "formik";
import { t } from "i18next";
import _ from "lodash";
import useMeaQuery from "qpr-base/apihooks/useMeaQuery";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import { getElementName } from "qpr-base/tplutils/utils";
import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import useFormStyles, { useButtonStyles } from "../KPIsForm/themes";
import DatePicker from "../components/DatePicker";
import {
  getMeasurementDetails,
  useMeasurementUnitQueries,
} from "../components/MeasurementUnit";
import StateTabs from "../components/StateTabs";
import FormikInputField from "./../form/FormikInputField";
import FormikSelect from "./../form/FormikSelect";
import SimpleFormTable, { SimpleCell } from "./formTable";

interface SubInfoProps {
  milestoneId: string;
  milestoneProjectId: string;
  name: string;
  isMileStone?: boolean;
  headers?: string[];
  save: any;
  close: any;
  initialValues: any;
  disabled?: boolean;
  projectDates: [string, string];
  formName: string;
  INITIAL_VALUES?: any;
  unit: {
    ar: string;
    "en-gb": string;
  };
  editing?: boolean;
  subkpiData?: any;
  topElements?: any;
  model2030?: any;
  currentMeaIds: string[];
  currentSymbols?: string[];
  selectedEditSymbol?: string;
  role?: string;
}
const SubKPIAdditionalFields = (props: {
  meaId: string;
  unitId: string;
  setField: any;
  setUnit: any;
  ctx: any;
  editing?: boolean;
  subkpiData?: any;
  topElements?: any;
  role?: string;
}) => {
  const ctx = useAppContext();

  const { translationsLoading, unitData, unitDataTranslations, unitLoading } =
    useMeasurementUnitQueries(ctx.fullModelId);

  useEffect(() => {
    if (translationsLoading) {
      return;
    }

    const { unit, unitTranslations } = getMeasurementDetails(
      props.unitId,
      unitData,
      unitDataTranslations
    );
    if (!unit || !unitTranslations) {
      return;
    }
    props.setField("unit", unit?.id || "");

    props.setUnit({
      ar: unitTranslations["Localized-Text-Attributes"].ar.Symbol,
      "en-gb": unitTranslations["Localized-Text-Attributes"]["en-gb"].Symbol,
    });
    props.setField("unitSymbol", {
      ar: unitTranslations["Localized-Text-Attributes"].ar.Symbol,
      "en-gb": unitTranslations["Localized-Text-Attributes"]["en-gb"].Symbol,
    });
  }, [props.unitId, unitDataTranslations, translationsLoading]);
  if (unitLoading || translationsLoading) {
    return <></>;
  }

  const translatedUnits = unitDataTranslations?.data[0]?.translations
    ? JSON.parse(unitDataTranslations?.data[0]?.translations)
    : "";

  const formattedValue = (e: any) => {
    const metadata = JSON.parse(e.translations);
    return {
      title: getElementName(e, props.ctx.language),
      value: metadata.ID,
    };
  };
  const GEOptions = props.topElements?.map((e: any) => {
    return formattedValue(e);
  });

  return (
    <div>
      <div>
        <FormikInputField
          name="symbol"
          label={t("Sub-KPI Symbol")}
          dir={ctx.rtl ? "rtl" : "ltr"}
          placeholder={t("Enter Sub-KPI Symbol")}
          disabled={!!props.meaId}
        />
      </div>
      <div>
        <FormikSelect
          options={
            unitData?.data
              .filter((unit) => unit.name !== "Date")
              .map((unit) => ({
                title: t(unit.name),
                value: unit.id,
              })) || []
          }
          placeholder="Select Measurement Unit"
          label={t("forms.Measurement Unit")}
          name="unit"
          handleFieldChange={(value: any, field: any) => {
            props.setField(field, value);
            const unit = unitData?.data.find((e: any) => e.id === value);
            const unitTranslations = translatedUnits.find(
              (unitTranslation: any) =>
                unitTranslation["Unit Name"] === unit.name
            );
            props.setField("unit", unit?.id || "");
            props.setUnit({
              ar: unitTranslations["Localized-Text-Attributes"].ar.Symbol,
              "en-gb":
                unitTranslations["Localized-Text-Attributes"]["en-gb"].Symbol,
            });
            props.setField("unitSymbol", {
              ar: unitTranslations["Localized-Text-Attributes"].ar.Symbol,
              "en-gb":
                unitTranslations["Localized-Text-Attributes"]["en-gb"].Symbol,
            });
          }}
        />
      </div>
      {props.role !== "__WF_GE_COORDINATOR" && (
        <div>
          <FormikSelect
            options={GEOptions}
            disabled={props.editing}
            placeholder="GE Owner Name"
            label={t("GE Owner Name")}
            name="GE ID"
            handleFieldChange={(value: any, field: any) => {
              props.setField(field, value);
            }}
          />
        </div>
      )}
    </div>
  );
};
const EditMilestoneForm = React.forwardRef((props: SubInfoProps, ref: any) => {
  const { name, isMileStone, save, close, model2030 } = props;
  const [unit, setUnit] = useState<{ ar: string; "en-gb": string }>(
    props.name === "Sub-KPI" ? { ar: "", "en-gb": "" } : props.unit
  );
  const ctx = useAppContext();
  const { initialValues } = props;

  const classes = useFormStyles();
  const buttonClasses = useButtonStyles();

  const lang = conf.getLanguages();
  const lang2Fields = [
    {
      name: `basicInformation.[${lang[1]}].Project Name`,
      label: `${t("milestones.Name", "Milestone Name")} (${t(lang[1])})`,
      placeholder: "",
      rows: 1,
      multiline: false,
    },
    {
      name: `basicInformation.[${lang[1]}].Milestone Description`,
      label: `${t("milestones.Description", "Milestone Description")} (${t(
        lang[1]
      )})`,
      placeholder: "",
      rows: 4,
      multiline: true,
    },
  ];
  const lang1Fields = [
    {
      name: `basicInformation.[${lang[0]}].Project Name`,
      label: `${t("milestones.Name", "Milestone Name")} (${t(lang[0])})`,
      placeholder: "",
      rows: 1,
      multiline: false,
    },
    {
      name: `basicInformation.[${lang[0]}].Milestone Description`,
      label: `${t("milestones.Description", "Milestone Description")} (${t(
        lang[0]
      )})`,
      placeholder: "",
      rows: 4,
      multiline: true,
    },
  ];
  const headers = props.headers || [];

  const Lang1Fields = () => (
    <>
      {lang1Fields.map((field) => (
        <FormikInputField
          name={field.name}
          label={field.label}
          placeholder={field.placeholder}
          multiline={field.multiline}
          rows={field.rows}
          dir={ctx.rtl ? "rtl" : "ltr"}
          disabled={props.disabled}
        />
      ))}
    </>
  );
  const Lang2Fields = () => (
    <>
      {lang2Fields.map((field) => (
        <FormikInputField
          name={field.name}
          label={field.label}
          dir={ctx.rtl ? "rtl" : "ltr"}
          placeholder={field.placeholder}
          rows={field.rows}
          multiline={field.multiline}
          disabled={props.disabled}
        />
      ))}
    </>
  );

  const minDate = (a: Date, b: Date) => (a < b ? a : b);
  const maxDate = (a: Date, b: Date) => (a > b ? a : b);
  const getStartDateMax = (endDate?: Date) => {
    if (props.projectDates[1]) {
      if (endDate) {
        return minDate(endDate, new Date(props.projectDates[1]));
      }
      return new Date(props.projectDates[1]);
    }
    return new Date("2040");
  };
  const getEndDateMin = (startDate?: Date) => {
    if (props.projectDates[0]) {
      if (startDate) {
        return maxDate(startDate, new Date(props.projectDates[0]));
      }
      return new Date(props.projectDates[0]);
    }
    return new Date("2010");
  };
  const FORM_VALIDATION_MILESTONE = yup.object().shape({
    ar: yup.object().shape({
      "Project Name": yup
        .string()
        .min(2, "Too Short!")
        .required(t("Required Field")),
    }),

    "Milestone planned start date": yup
      .string()
      .nullable()
      .required(t("Required Field")),
    "Milestone planned end date": yup
      .string()
      .nullable()
      .required(t("Required Field")),
    "Milestone weight": yup
      .number()
      .required(t("Required Field"))
      .transform((num) => (Number.isNaN(num) ? undefined : num)),
  });

  const FORM_VALIDATION = yup.object().shape({});

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        // save(values);
      }}
      validationSchema={
        props.isMileStone ? FORM_VALIDATION_MILESTONE : FORM_VALIDATION
      }
      validateOnBlur
      innerRef={ref}
    >
      {({
        handleSubmit,
        values,
        touched,
        errors,
        setFieldError,
        setFieldValue,
      }) => (
        <Form>
          <div className={classes.formWrapper}>
            <div className={classes.formColumn}>
              {ctx.rtl ? <Lang2Fields /> : <Lang1Fields />}
              {props.isMileStone && (
                <DatePicker
                  name="Milestone planned start date"
                  placeholder={t("Select Start Date")}
                  label={t("Milestone planned start date")}
                  disabled={props.disabled}
                  minDate={
                    props.projectDates[0]
                      ? new Date(props.projectDates[0])
                      : new Date("2010")
                  }
                  maxDate={getStartDateMax(
                    values["Milestone planned end date"]
                  )}
                />
              )}
            </div>
            <div className={classes.formColumn}>
              {ctx.rtl ? <Lang1Fields /> : <Lang2Fields />}
              {props.isMileStone && (
                <>
                  <DatePicker
                    name="Milestone planned end date"
                    placeholder={t("Select End Date")}
                    label={t("Milestone planned end date")}
                    disabled={props.disabled}
                    minDate={getEndDateMin(
                      values["Milestone planned start date"]
                    )}
                    maxDate={
                      props.projectDates[1]
                        ? new Date(props.projectDates[1])
                        : new Date("2040")
                    }
                  />
                </>
              )}
            </div>
          </div>
          {props.name === "Sub-KPI" && (
            <SubKPIAdditionalFields
              {...props}
              ctx={ctx}
              meaId={values.meaId}
              setField={setFieldValue}
              setUnit={setUnit}
              unitId={values.unit}
            />
          )}

          {props.isMileStone && (
            <div style={{ marginTop: "-40px" }}>
              <FormikInputField
                name="Milestone weight"
                label={t("Milestone weight")}
                suffix="%"
                type="number"
                disabled={props.disabled}
                dir={ctx.rtl ? "rtl" : "ltr"}
              />
            </div>
          )}
          <div style={{ marginTop: "16px", width: "70.8vw" }}>
            <SimpleFormTable
              headers={[t("VALUE NAME"), ...(headers || [])]}
              rows={
                props.isMileStone
                  ? [
                      [
                        t("Milestone Progress %"),
                        ...(headers || []).map(
                          (periodName, ind) =>
                            ({
                              type: "numberField",
                              name: `values[COMPLETION_PERCENTAGE###${periodName}]`,
                              // name: `milestoneValues[${ind}].COMPLETION_PERCENTAGE###${periodName}`,
                              disabled: false,
                              suffix: "%",
                            } as SimpleCell)
                        ),
                      ],
                      [
                        t("Milestone Planned Progress %"),
                        ...(headers || []).map(
                          (periodName, ind) =>
                            ({
                              type: "numberField",
                              name: `values[PLANNED_COMPLETION###${periodName}]`,
                              // name: `milestoneValues[${ind}].PLANNED_COMPLETION###${periodName}`,
                              suffix: "%",
                            } as SimpleCell)
                        ),
                      ],
                      [
                        t("Milestone Actual Start Date"),
                        ...(headers || []).map(
                          (periodName, ind) =>
                            ({
                              type: "dateField",
                              name: `values[ACTUAL_START_DATE###${periodName}]`,
                              // name: `milestoneValues[${ind}].ACTUAL_START_DATE###${periodName}`,
                              suffix: "%",
                              disabled: false,
                              minDate: values["Milestone planned start date"]
                                ? values["Milestone planned start date"]
                                : new Date("2010"),
                              maxDate: minDate(
                                values["Milestone planned end date"] ||
                                  new Date("2040"),
                                (values.milestoneValues[ind] ?? {
                                  "Project End Date": "",
                                })["Project End Date"] || new Date("2040")
                              ),
                            } as SimpleCell)
                        ),
                      ],
                      [
                        t("Milestone Actual End Date"),
                        ...(headers || []).map(
                          (periodName, ind) =>
                            ({
                              type: "dateField",
                              name: `values[ACTUAL_END_DATE###${periodName}]`,
                              // name: `milestoneValues[${ind}].ACTUAL_END_DATE###${periodName}`,
                              suffix: "%",
                              disabled: false,
                              /*
                              minDate: maxDate(
                                values["Milestone planned start date"] ||
                                  new Date("2010"),
                                (values.values[ind] ?? {
                                  "Project Start Date": "",
                                })["Project Start Date"] || new Date("2040")
                              ),
                              maxDate:
                                values["Milestone planned end date"] ||
                                new Date("2040"),
                              */
                            } as SimpleCell)
                        ),
                      ],
                    ]
                  : [
                      [
                        t("Actual"),
                        ...(headers || []).map((year, ind) => {
                          const cellUnit = values.unitSymbol ||
                            props.unit || { "en-gb": "", ar: "" };
                          return {
                            type: "numberField",
                            name: `values[${ind}].ACT`,
                            suffix: cellUnit[ctx.rtl ? "ar" : "en-gb"],
                            disabled:
                              parseInt(year, 10) >= new Date().getFullYear(),
                          } as SimpleCell;
                        }),
                      ],
                    ]
              }
              disabled={
                (props.isMileStone &&
                  (values["Milestone planned start date"] === undefined ||
                    values["Milestone planned end date"] === undefined)) ||
                props.disabled
              }
            />
          </div>

          <div className={classes.optionsContainer}>
            <Button
              className={clsx(buttonClasses.button, buttonClasses.cancelButton)}
              onClick={() => close()}
            >
              {t("Cancel")}
            </Button>

            <Button
              className={clsx(buttonClasses.button, buttonClasses.submitButton)}
              onClick={async () => {
                save(values);
                // handleSubmit();
              }}
              disabled={props.disabled}
            >
              {t("Save")}
            </Button>
          </div>
          {/*
            <div style={{ display: "flex" }}>
              <pre>{JSON.stringify(values, null, 2)}</pre>
            </div>
          */}
        </Form>
      )}
    </Formik>
  );
});

const SubKPIPicker = ({
  subkpiData,
  allSubKPIs,
  save,
  close,
  headers,
  disabled,
  currentMeaIds,
  formName,
  selectedGESymbol,
}: {
  subkpiData?: any;
  allSubKPIs?: any;
  save: any;
  close: any;
  headers?: string[];
  disabled?: boolean;
  currentMeaIds: string[];
  selectedGESymbol?: string;
  formName: string;
}) => {
  const classes = useFormStyles();
  const buttonClasses = useButtonStyles();
  const ctx = useAppContext();
  const [selectedGE, setSelectedGE] = useState("undefined");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedSubKPI, setSelectedSubKPI] = useState<any>(null);
  const [unit, setUnit] = useState<{ ar: string; "en-gb": string }>({
    ar: "",
    "en-gb": "",
  });

  const sameOwner = useMemo(() => {
    if (formName === "Sub-KPIs") {
      if (selectedGESymbol) {
        return [selectedGE, `GE_${selectedGE}`].includes(selectedGESymbol);
      }
      return false;
    }
    return true;
  }, [selectedGE, selectedGESymbol]);

  const getSubKPIIds = (GEId: string): string => {
    if (!allSubKPIs) return "";
    const resultArr: string[] = [];
    for (let i = 0; i < allSubKPIs.data.length; i += 1) {
      const metadataObj = allSubKPIs.data[i].translations
        ? JSON.parse(allSubKPIs.data[i].translations)
        : {};
      const SCOwner = metadataObj["Foreign-Keys"]["GE ID"]
        ? `GE_${metadataObj["Foreign-Keys"]["GE ID"]}`
        : "";
      if ((SCOwner === "" && GEId === "no-owner") || SCOwner === GEId)
        resultArr.push(allSubKPIs.data[i].id);
    }
    const result = resultArr.join(",");
    return result;
  };

  const { data: subkpis, isLoading } = useMeaQuery<any[]>(
    `[${getSubKPIIds(selectedGE)}]`,
    `id, name,typename,related(relation=parentObjects,as="parentid"),symbol,measureunitid,value(series="JS_ATTR", period="firstforseries",as="translations")${
      headers ? "," : ""
    }${
      headers
        ?.map(
          (year) =>
            `measure.value(period="${year}",series="ACT",as="ACT_${year}")`
        )
        .toString() || ""
    },id(as="id_of_original")`
  );

  const { translationsLoading, unitData, unitDataTranslations, unitLoading } =
    useMeasurementUnitQueries(ctx.fullModelId);
  useEffect(() => {
    if (translationsLoading) {
      return;
    }

    const { unit, unitTranslations } = getMeasurementDetails(
      selectedUnit,
      unitData,
      unitDataTranslations
    );
    if (!unit || !unitTranslations) {
      return;
    }

    setUnit({
      ar: unitTranslations["Localized-Text-Attributes"].ar.Symbol,
      "en-gb": unitTranslations["Localized-Text-Attributes"]["en-gb"].Symbol,
    });
  }, [selectedUnit, unitDataTranslations]);
  if (unitLoading || translationsLoading) {
    return <></>;
  }

  const GEs = (): any[] => {
    const result = subkpiData.map((GE: any) => ({
      title: getElementName(GE, ctx.language),
      value: GE.symbol,
    }));
    return result;
  };

  const SubKPIOptions =
    _.uniqBy(subkpis?.data, "id")
      .filter((subkpi: any) => !currentMeaIds.includes(subkpi.id))
      .map((subkpi: any) => ({
        title: getElementName(subkpi, ctx.language),
        value: JSON.stringify(subkpi),
      })) || [];
  const validationSchema = yup.object().shape({
    GE: yup.string().required(t("Required Field!")),
  });

  return (
    <Formik
      initialValues={{
        GE: "",
        subkpi: "",
        meaId: "",
        symbol: "",
        status: "modify",
        "GE ID": "",
        unit: "",
        "en-gb": {
          Name: "",
          Description: "",
        },
        ar: {
          Name: "",
          Description: "",
        },
        values: [],
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        save(values);
      }}
      validateOnBlur
    >
      {({ handleSubmit, values, touched, errors, setFieldValue }) => (
        <Form>
          <FormikSelect
            name="GE"
            label={t("Government Entity")}
            options={GEs()}
            handleFieldChange={(value, name) => {
              setSelectedSubKPI(null);
              setFieldValue("GE", value);
              setSelectedGE(value);
              setFieldValue("subkpi", "");
            }}
          />
          <FormikSelect
            name="subkpi"
            label={t("Sub-KPI")}
            options={SubKPIOptions}
            handleFieldChange={(unparsedValue: any, name) => {
              const value = JSON.parse(unparsedValue);
              setSelectedSubKPI(value);
              const JS_ATTR = JSON.parse(value.translations)[
                "Localized-Text-Attributes"
              ];
              setFieldValue("subkpi", unparsedValue);
              setFieldValue("en-gb.Name", JS_ATTR["en-gb"].Name);
              setFieldValue("en-gb.Description", JS_ATTR["en-gb"].Description);
              setFieldValue("ar.Name", JS_ATTR.ar.Name);
              setFieldValue("ar.Description", JS_ATTR.ar.Description);
              // setFieldValue('meaId', value.id);
              setFieldValue("symbol", value.symbol);
              setFieldValue(
                "unit",
                value.measureunitid?.split(".")?.length === 3
                  ? value.measureunitid
                  : ""
              );
              setFieldValue("id_of_original", value.id_of_original);
              const selectedGE = subkpiData.find(
                (e: any) => e.symbol === values.GE
              );
              const ge_metadata = JSON.parse(selectedGE.translations);

              setFieldValue("GE ID", ge_metadata.ID);
              setSelectedUnit(value.measureunitid);
              headers?.forEach((year, ind) => {
                if (value[`ACT_${year}`] !== undefined) {
                  setFieldValue(`values[${ind}].ACT`, value[`ACT_${year}`]);
                } else {
                  setFieldValue(`values[${ind}].ACT`, "");
                }
              });

              if (translationsLoading) {
                return;
              }

              const { unit, unitTranslations } = getMeasurementDetails(
                value.measureunitid,
                unitData,
                unitDataTranslations
              );
              if (!unit || !unitTranslations) {
                return;
              }

              setFieldValue("unitSymbol", {
                ar: unitTranslations["Localized-Text-Attributes"].ar.Symbol,
                "en-gb":
                  unitTranslations["Localized-Text-Attributes"]["en-gb"].Symbol,
              });
            }}
            disabled={!SubKPIOptions.length}
          />

          {
            // feature flag for additional fields in edit mode
            false && (
              <TableRow>
                <p style={{ fontSize: "16px" }}>
                  {t("Symbol")}: {selectedSubKPI.symbol}
                </p>
                <p style={{ fontSize: "16px" }}>
                  {t("forms.Measurement Unit")}:{" "}
                  {unit ? unit[ctx.rtl ? "ar" : "en-gb"] : "-"}
                </p>
                <p style={{ fontSize: "16px" }}>
                  {t("Formula Reference")}:{" "}
                  {`DP2030_SUBKPIS.${selectedSubKPI.symbol}.ACT`}
                </p>
              </TableRow>
            )
          }

          <div style={{ marginTop: "16px", width: "73vw" }}>
            <SimpleFormTable
              headers={[t("VALUE NAME"), ...(headers || [])]}
              rows={[
                [
                  t("Actual"),
                  ...(headers?.map(
                    (year, ind) =>
                      ({
                        type: "numberField",
                        name: `values[${ind}].ACT`,
                        suffix: unit[ctx.rtl ? "ar" : "en-gb"],
                        value: selectedSubKPI
                          ? selectedSubKPI[`ACT_${year}`] || ""
                          : "",
                        disabled:
                          parseInt(year, 10) >= new Date().getFullYear(),
                      } as SimpleCell)
                  ) || []),
                ],
              ]}
              disabled={disabled || !values.subkpi || !sameOwner}
            />
          </div>
          <div className={classes.optionsContainer}>
            <Button
              className={clsx(buttonClasses.button, buttonClasses.cancelButton)}
              onClick={() => close()}
            >
              {t("Cancel")}
            </Button>

            <Button
              className={clsx(buttonClasses.button, buttonClasses.submitButton)}
              onClick={() => handleSubmit()}
              disabled={disabled}
            >
              {t("Save")}
            </Button>
          </div>
          {/* <div>
            <pre>{JSON.stringify({ ...values, subkpi: '' }, null, 2)}</pre>
          </div> */}
        </Form>
      )}
    </Formik>
  );
};

const SubItemForm = React.forwardRef((props: any, ref) => {
  const classes = useFormStyles();
  const [state, setState] = useState("new");
  const ctx = useAppContext();
  return (
    <div className={classes.dashedWrapper}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h1 className={classes.formTitle}>
          {t(
            `${props.editing || state === "existing" ? "" : "New "}${
              props.name
            }`
          )}
        </h1>
        {props.name === "Sub-KPI" && !props.editing && (
          <StateTabs
            name="subKPIFormPicker"
            options={[
              { value: "new", title: t("CREATE NEW SUB-KPI") },
              { value: "existing", title: t("SELECT FROM EXISTING SUB-KPIS") },
            ]}
            setState={setState}
            selected={state}
          />
        )}
      </div>
      {state === "existing" ? (
        <SubKPIPicker
          subkpiData={props.subkpiData}
          allSubKPIs={props.allSubKPIs}
          modelId={ctx.fullModelId}
          {...props}
        />
      ) : (
        <EditMilestoneForm {...props} ref={ref} />
      )}
    </div>
  );
});

export default SubItemForm;
